import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Pencil, Plus, Check } from "lucide-react";

const PALETTE = ["#166534", "#b45309", "#1d4ed8", "#be123c", "#7c3aed", "#0891b2", "#65a30d", "#db2777", "#475569", "#ea580c"];

export default function ManageTagsDialog({ open, onOpenChange, library, onChanged }) {
  const [label, setLabel] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [editId, setEditId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const reset = () => { setLabel(""); setColor(PALETTE[0]); setEditId(null); };

  const submit = async () => {
    if (!label.trim()) return toast.error("Enter a tag name");
    try {
      if (editId) await api.put(`/customer-tags/${editId}`, { label, color });
      else await api.post("/customer-tags", { label, color });
      toast.success(editId ? "Tag updated" : "Tag created");
      reset(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startEdit = (t) => { setEditId(t.id); setLabel(t.label); setColor(t.color); };

  const del = async (id) => {
    try {
      await api.delete(`/customer-tags/${id}`);
      toast.success("Tag deleted");
      if (editId === id) reset();
      setConfirmId(null);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="manage-tags-dialog">
        <DialogHeader><DialogTitle className="font-display">Manage Tags</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2 rounded-lg border border-border p-3">
            <Input
              data-testid="tag-label-input"
              placeholder="Tag name (e.g. Winter season)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <div className="flex flex-wrap gap-1.5">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`tag-color-${c}`}
                  onClick={() => setColor(c)}
                  className="h-6 w-6 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  style={{ backgroundColor: c, outline: color === c ? `2px solid ${c}` : "none", outlineOffset: 2 }}
                >
                  {color === c && <Check className="h-3.5 w-3.5 text-white" />}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button data-testid="save-tag-btn" onClick={submit} className="bg-primary text-primary-foreground hover:bg-[#152e17]">
                {editId ? <><Check className="h-4 w-4 mr-1" />Update</> : <><Plus className="h-4 w-4 mr-1" />Add tag</>}
              </Button>
              {editId && <Button variant="ghost" onClick={reset}>Cancel</Button>}
            </div>
          </div>

          <div className="max-h-64 overflow-auto space-y-1">
            {library.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No tags yet. Create your first above.</p>
            ) : library.map((t) => (
              <div key={t.id} data-testid={`tag-row-${t.id}`} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.color }} />
                  {t.label}
                </span>
                {confirmId === t.id ? (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-muted-foreground">Delete?</span>
                    <button data-testid={`confirm-delete-tag-${t.id}`} onClick={() => del(t.id)} className="font-semibold text-destructive hover:underline">Yes</button>
                    <button onClick={() => setConfirmId(null)} className="text-muted-foreground hover:underline">No</button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <button data-testid={`edit-tag-${t.id}`} onClick={() => startEdit(t)} className="p-1.5 rounded hover:bg-muted"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    <button data-testid={`delete-tag-${t.id}`} onClick={() => setConfirmId(t.id)} className="p-1.5 rounded hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
